import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';

export default function LivePollBoard() {
	const [code, setCode] = useState('');
	const [session, setSession] = useState(null);
	const [questions, setQuestions] = useState([]);
	const [activeQuestion, setActiveQuestion] = useState(null);
	const [groupCounts, setGroupCounts] = useState([]);
	const [answers, setAnswers] = useState([]);
	const [error, setError] = useState('');
	const evtRef = useRef(null);

	const container = { padding: 24, maxWidth: 1220, margin: '0 auto' };
	const card = { background: 'var(--background-secondary)', border: '1px solid var(--border-color)', borderRadius: 0, padding: 16 };
	const input = { width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 0, background: 'var(--background-secondary)' };
	const button = { padding: '10px 14px', borderRadius: 0, border: 'none', background: 'var(--primary-color)', color: 'var(--text-inverse)', fontWeight: 600, cursor: 'pointer', boxShadow: '0 8px 20px rgba(216,184,137,0.35)' };

	async function loadState(sessionCode) {
		try {
			const data = await api.request(`/polls/state/${sessionCode}`);
			setSession(data.session);
			setQuestions(data.questions || []);
			setActiveQuestion(data.activeQuestion || null);
			setGroupCounts(data.groupCounts || []);
			setAnswers(Array.isArray(data.answers) ? data.answers : []);
		} catch (e) {
			setError(e.message || 'Failed to load');
		}
	}

	const connectStream = (sessionCode) => {
		if (evtRef.current) {
			evtRef.current.close();
			evtRef.current = null;
		}
		const token = localStorage.getItem('faithinsight_token');
		const es = new EventSource(`${api.baseURL}/polls/stream/${sessionCode}?token=${encodeURIComponent(token || '')}`, { withCredentials: false });
		es.onmessage = (evt) => {
			try {
				const data = JSON.parse(evt.data || '{}');
				// Incremental UI update for answers without a full reload
				if (data && data.type === 'answer') {
					// Only update if the answer belongs to the current active question
					if (session && session.activeQuestionId && activeQuestion && Number(data.questionId) === Number(activeQuestion.id)) {
						// Update answers list immediately
						setAnswers(prev => ([...prev, { id: `temp_${Date.now()}`, group_label: data.groupLabel || null, answer_text: data.answerText || '' }]));
						// Update group counts immediately
						setGroupCounts(prev => {
							const next = Array.isArray(prev) ? [...prev] : [];
							const idx = next.findIndex(r => (r.group_label || null) === (data.groupLabel || null));
							if (idx >= 0) {
								next[idx] = { ...next[idx], count: Number(next[idx].count || 0) + 1 };
							} else {
								next.push({ group_label: data.groupLabel || null, count: 1 });
							}
							return next;
						});
						return;
					}
				}
				// Fallback: reload state to stay consistent
				loadState(sessionCode);
			} catch (e) {
				// If parsing fails, reload the state
				loadState(sessionCode);
			}
		};
		es.onerror = () => {
			// Let the browser attempt to reconnect automatically
		};
		evtRef.current = es;
	};

	const grouped = useMemo(() => {
		const map = {};
		for (const row of groupCounts) {
			const key = row.group_label || 'Unassigned';
			map[key] = (map[key] || 0) + Number(row.count || 0);
		}
		return map;
	}, [groupCounts]);

	const handleJoin = async (e) => {
		e.preventDefault();
		const sc = code.toUpperCase();
		await loadState(sc);
		connectStream(sc);
	};

	const setActive = async (qid) => {
		try {
			await api.request(`/polls/activate/${qid}`, { method: 'POST' });
			await loadState(session.code);
		} catch (e) {
			setError(e.message || 'Failed to activate');
		}
	};

	useEffect(() => () => { if (evtRef.current) evtRef.current.close(); }, []);

	// Auto-join when opened with ?code=XYZ
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const c = (params.get('code') || '').toUpperCase();
		if (c) {
			setCode(c);
			loadState(c);
			connectStream(c);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div style={container}>
			<div style={{ marginBottom: 16 }}>
				<h1 style={{ fontSize: 32, lineHeight: 1.2, margin: 0 }}>Live Poll Board</h1>
				<p style={{ color: 'var(--text-secondary)', marginTop: 6 }}>Activate questions and view all answers in real time</p>
			</div>
			{!session && (
				<div style={card}>
					<form onSubmit={handleJoin}>
						<div style={{ display: 'grid', gridTemplateColumns: '300px auto', gap: 12 }}>
							<div>
								<label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Session Code</label>
								<input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="e.g. ABC123" style={input} />
							</div>
							<div style={{ display: 'flex', alignItems: 'flex-end' }}>
								<button type="submit" style={button}>Open</button>
							</div>
						</div>
					</form>
				</div>
			)}
			{session && (
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16 }}>
					<div>
						<div style={{ ...card, marginBottom: 12 }}>
							<div style={{ fontWeight: 600 }}>
								<span>Session:</span> <span>{session.title} ({session.code})</span>
							</div>
						</div>
						<div style={{ ...card, fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
							{activeQuestion ? activeQuestion.question_text : 'No active question'}
						</div>
						<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
							{Object.entries(grouped).map(([g, c]) => (
								<div key={g} style={{ ...card, minWidth: 140, textAlign: 'center' }}>
									<div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{g}</div>
									<div style={{ fontSize: 28, fontWeight: 800 }}>{c}</div>
								</div>
							))}
						</div>
						<div style={{ ...card, marginTop: 12 }}>
							<div style={{ fontWeight: 700, marginBottom: 8 }}>All Answers</div>
							{answers && answers.length > 0 ? (
								<div style={{ display: 'grid', gap: 8 }}>
									{answers.map(a => (
										<div key={a.id} style={{ padding: 10, border: '1px solid var(--border-color)', borderRadius: 0, background: 'var(--background-secondary)' }}>
											<div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{a.group_label || 'Unassigned'}</div>
											<div style={{ whiteSpace: 'pre-wrap' }}>{a.answer_text}</div>
										</div>
									))}
								</div>
							) : (
								<div style={{ color: 'var(--text-secondary)' }}>No answers yet.</div>
							)}
						</div>
					</div>
					<div>
						<div style={{ fontWeight: 700, marginBottom: 8 }}>Questions</div>
						<div style={{ display: 'grid', gap: 8 }}>
							{questions.map(q => (
								<button key={q.id} onClick={() => setActive(q.id)} style={{ ...card, padding: 10, background: q.id === session.activeQuestionId ? 'rgba(216,184,137,0.08)' : 'var(--background-secondary)', textAlign: 'left', cursor: 'pointer' }}>
									{q.question_text}
								</button>
							))}
						</div>
					</div>
				</div>
			)}
			{error ? <div style={{ color: 'var(--error-color)', marginTop: 8, fontWeight: 600 }}>{error}</div> : null}
		</div>
	);
}


